import React, { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle } from "lucide-react";

export default function AdminStudentsPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) navigate("/");
  }, [loading, user, navigate]);

  const refresh = () => api.get("/admin/students").then((r) => setRows(r.data));

  useEffect(() => {
    if (user && user.role === "admin") refresh();
  }, [user]);

  const decide = async (user_id, approve) => {
    setBusy(true);
    try {
      await api.post("/admin/students/decide", { user_id, approve });
      toast.success(approve ? "Estudiante verificado" : "Solicitud rechazada");
      refresh();
    } finally {
      setBusy(false);
    }
  };

  if (loading || !user || user.role !== "admin") return null;

  return (
    <div className="bg-[#F8FAFC] min-h-screen">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-12">
        <p className="eyebrow mb-2">Admin · Verificación de estudiantes</p>
        <h1 className="font-serif text-4xl font-semibold text-[#1A1A1A] mb-8">Solicitudes de estudiante</h1>
        {rows.length === 0 ? (
          <p className="text-[#475569]">No hay solicitudes.</p>
        ) : (
          <div className="space-y-3">
            {rows.map((r) => (
              <div key={r.id} className="bg-white border border-[#E2E8F0] p-5 grid gap-4 md:grid-cols-12 items-center">
                <div className="md:col-span-5">
                  <p className="font-serif text-xl font-semibold text-[#1A1A1A]">{r.name}</p>
                  <p className="text-sm text-[#475569]">{r.email}</p>
                  {r.student_institution && <p className="text-xs text-[#64748B] mt-1">{r.student_institution}</p>}
                </div>
                <div className="md:col-span-4">
                  {r.student_document && (
                    <a
                      href={fileUrl(r.student_document)}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="text-sm text-[#3B5FB8] link-underline"
                    >
                      Ver documento →
                    </a>
                  )}
                </div>
                <div className="md:col-span-3 flex gap-2 justify-end">
                  <span
                    className={`px-2 py-1 text-[10px] font-mono uppercase tracking-widest border ${
                      r.student_status === "verified"
                        ? "cat-apuntes"
                        : r.student_status === "rejected"
                        ? "cat-negocios"
                        : "cat-pisos"
                    }`}
                  >
                    {r.student_status}
                  </span>
                  {r.student_status !== "verified" && (
                    <Button
                      size="sm"
                      onClick={() => decide(r.id, true)}
                      disabled={busy}
                      className="rounded-none bg-[#2D5A3F] hover:bg-[#204833] text-[#F8FAFC]"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-1" /> Aprobar
                    </Button>
                  )}
                  {r.student_status !== "rejected" && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => decide(r.id, false)}
                      disabled={busy}
                      className="rounded-none border-[#CBD5E1]"
                    >
                      <XCircle className="w-4 h-4 mr-1" /> Rechazar
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
